import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  ChevronLeft, ChevronRight, Download, RefreshCw, Pencil,
  Calculator, AlertTriangle, Check
} from "lucide-react";
import { MONTH_NAMES, formatCurrency, API_URL } from "../lib/utils";
import { Checkbox } from "../components/ui/checkbox";


export default function PayrollsPage() {
  const { companies, locations, fetchCompanies, fetchLocations } = useApp();
  const [employees, setEmployees] = useState([]);
  const [payrolls, setPayrolls] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [editForm, setEditForm] = useState({
    gross_pay: "",
    net_pay: "",
    holiday_days_to_deduct: "",
    rol_hours_to_deduct: "",
    sick_days: "",
    days_104: "",
    lutto_days: "",
    matrimoniale_days: "",
    congedo_parentale_days: "",
    km_reimbursement: "",
    expense_reimbursement: "",
    advances_paid: "",
    nova_service_contribution: "",
    notes: ""
  });

  const [novaServiceSafety, setNovaServiceSafety] = useState(0);
  const [nsManagementFee, setNsManagementFee] = useState(null);
  const [nsManagementLabel, setNsManagementLabel] = useState("");

  const selectedCompanyName = companies.find(c => c.id === filterCompany)?.name || "";
  const isNovaService = selectedCompanyName.toLowerCase().includes("nova service");

  // Load NS monthly data when filters change
  useEffect(() => {
    if (isNovaService && filterLocation && year && month) {
      axios.get(`${API_URL}/api/payrolls/ns-monthly`, {
        params: { year, month, location_id: filterLocation }, withCredentials: true
      }).then(res => {
        setNovaServiceSafety(res.data.safety_amount || 0);
        setNsManagementFee(res.data.management_fee);
        setNsManagementLabel(res.data.management_fee_label || "");
      }).catch(() => {});
    }
  }, [isNovaService, filterLocation, year, month]);

  const saveNsMonthly = (safetyVal, mgmtVal, labelVal) => {
    if (!filterLocation) return;
    axios.post(`${API_URL}/api/payrolls/ns-monthly`, {
      safety_amount: safetyVal, management_fee: mgmtVal, management_fee_label: labelVal || null
    }, {
      params: { year, month, location_id: filterLocation }, withCredentials: true
    }).catch(() => {});
  };

  useEffect(() => {
    fetchCompanies();
    fetchLocations();
  }, [fetchCompanies, fetchLocations]);

  useEffect(() => {
    if (filterLocation) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, filterLocation]);

  const loadData = async () => {
    if (!filterLocation) return;
    setLoading(true);
    try {
      // Fetch employees
      const empResponse = await axios.get(`${API_URL}/api/employees`, { 
        params: { location_id: filterLocation },
        withCredentials: true 
      });
      setEmployees(empResponse.data);

      // Fetch payrolls
      const payResponse = await axios.get(`${API_URL}/api/payrolls`, {
        params: { year, month, location_id: filterLocation },
        withCredentials: true
      });

      const payMap = {};
      payResponse.data.forEach(p => {
        payMap[p.employee_id] = p;
      });
      setPayrolls(payMap);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const handleGeneratePayrolls = async () => {
    if (!filterLocation) return;
    setGenerating(true);
    try {
      await axios.get(`${API_URL}/api/payrolls/generate/${year}/${month}`, {
        params: { location_id: filterLocation },
        withCredentials: true
      });
      loadData();
    } catch (err) {
      alert("Errore nella generazione: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const [generatingPrev, setGeneratingPrev] = useState(false);

  const handleGenerateFromPrevious = async () => {
    if (!filterLocation) return;
    setGeneratingPrev(true);
    try {
      await axios.post(`${API_URL}/api/payrolls/generate-from-previous`, null, {
        params: { location_id: filterLocation, year, month, is_nova_service: isNovaService },
        withCredentials: true
      });
      loadData();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setGeneratingPrev(false);
    }
  };

  const handleOpenEditDialog = (employeeId) => {
    const payroll = payrolls[employeeId];
    const employee = employees.find(e => e.id === employeeId);
    const defaultNetPay = employee?.base_salary?.toString() || "";
    setEditingPayroll({ employeeId, payroll });
    setEditForm({
      gross_pay: payroll?.gross_pay?.toString() || "",
      net_pay: payroll?.net_pay?.toString() || defaultNetPay,
      holiday_days_to_deduct: payroll?.holiday_days_to_deduct?.toString() || "",
      rol_hours_to_deduct: payroll?.rol_hours_to_deduct?.toString() || "",
      sick_days: payroll?.sick_days?.toString() || "",
      days_104: payroll?.days_104?.toString() || "",
      lutto_days: payroll?.lutto_days?.toString() || "",
      matrimoniale_days: payroll?.matrimoniale_days?.toString() || "",
      congedo_parentale_days: payroll?.congedo_parentale_days?.toString() || "",
      km_reimbursement: payroll?.km_reimbursement?.toString() || "",
      expense_reimbursement: payroll?.expense_reimbursement?.toString() || "",
      advances_paid: payroll?.advances_paid?.toString() || "",
      nova_service_contribution: payroll?.nova_service_contribution?.toString() || "",
      notes: payroll?.notes || ""
    });
    setEditDialogOpen(true);
  };

  const handleSavePayroll = async () => {
    if (!editingPayroll) return;
    try {
      const data = {
        employee_id: editingPayroll.employeeId,
        year,
        month,
        gross_pay: editForm.gross_pay ? parseFloat(editForm.gross_pay) : null,
        net_pay: editForm.net_pay ? parseFloat(editForm.net_pay) : null,
        holiday_days_to_deduct: editForm.holiday_days_to_deduct ? parseFloat(editForm.holiday_days_to_deduct) : null,
        rol_hours_to_deduct: editForm.rol_hours_to_deduct ? parseFloat(editForm.rol_hours_to_deduct) : null,
        sick_days: editForm.sick_days ? parseInt(editForm.sick_days) : null,
        days_104: editForm.days_104 ? parseInt(editForm.days_104) : null,
        lutto_days: editForm.lutto_days ? parseInt(editForm.lutto_days) : null,
        matrimoniale_days: editForm.matrimoniale_days ? parseInt(editForm.matrimoniale_days) : null,
        congedo_parentale_days: editForm.congedo_parentale_days ? parseInt(editForm.congedo_parentale_days) : null,
        km_reimbursement: editForm.km_reimbursement ? parseFloat(editForm.km_reimbursement) : null,
        expense_reimbursement: editForm.expense_reimbursement ? parseFloat(editForm.expense_reimbursement) : null,
        advances_paid: editForm.advances_paid ? parseFloat(editForm.advances_paid) : null,
        nova_service_contribution: editForm.nova_service_contribution ? parseFloat(editForm.nova_service_contribution) : null,
        notes: editForm.notes || null
      };

      const response = await axios.post(`${API_URL}/api/payrolls`, data, { withCredentials: true });
      setPayrolls(prev => ({
        ...prev,
        [editingPayroll.employeeId]: response.data
      }));
      setEditDialogOpen(false);
    } catch (err) {
      alert("Errore nel salvataggio: " + (err.response?.data?.detail || err.message));
    }
  };


  const handleTogglePaid = async (employeeId) => {
    const payroll = payrolls[employeeId];
    if (!payroll) return;
    try {
      const data = {
        ...payroll,
        is_paid: !payroll.is_paid
      };
      const response = await axios.post(`${API_URL}/api/payrolls`, data, { withCredentials: true });
      setPayrolls(prev => ({ ...prev, [employeeId]: response.data }));
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const unpaidCount = Object.values(payrolls).filter(p => !p.is_paid && p.net_pay).length;
  const isPayAlert = new Date().getDate() >= 10 && unpaidCount > 0;

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/payroll/${year}/${month}`, {
        params: { location_id: filterLocation },
        responseType: 'blob',
        withCredentials: true
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stipendi_${year}_${month.toString().padStart(2, '0')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Errore nell'export: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleExportNovaServicePDF = async () => {
    try {
      const params = { year, month };
      if (filterLocation) params.location_id = filterLocation;
      const response = await axios.get(`${API_URL}/api/payrolls/export/nova-service-pdf`, {
        params,
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conteggi_novaservice_${year}_${month.toString().padStart(2, '0')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Errore nell'export: " + (err.response?.data?.detail || err.message));
    }
  };


  const filteredLocations = filterCompany 
    ? locations.filter(l => l.company_id === filterCompany)
    : locations;

  const getPayrollData = (employeeId) => {
    return payrolls[employeeId] || {};
  };

  const calculateGrandTotal = () => {
    return Object.values(payrolls).reduce((sum, p) => sum + (p.total || 0), 0);
  };

  const novaServiceCalc = (() => {
    if (!isNovaService) return null;
    const totalLordo = Object.values(payrolls).reduce((sum, p) => sum + (p.gross_pay || 0), 0);
    const totalContributions = Object.values(payrolls).reduce((sum, p) => sum + (p.nova_service_contribution || 0), 0);
    const safetyFee = totalLordo * 0.02;
    const managementFee = nsManagementFee || 0;
    const subtotal = totalLordo + totalContributions + safetyFee + managementFee;
    const iva = subtotal * 0.22;
    const grandTotal = subtotal + iva;
    return { totalLordo, totalContributions, safetyFee, managementFee, subtotal, iva, grandTotal };
  })();

  return (
    <div className="space-y-6" data-testid="payrolls-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">
            Stipendi
          </h1>
          <p className="text-[#52525B] font-ibm-plex mt-1">
            Gestisci i cedolini mensili
          </p>
        </div>
        {filterLocation && (
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="generate-from-previous-button"
              onClick={handleGenerateFromPrevious}
              disabled={generatingPrev}
              variant="outline"
              className="font-ibm-plex"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${generatingPrev ? "animate-spin" : ""}`} />
              {generatingPrev ? "Generazione..." : "Genera da Mese Precedente"}
            </Button>
            <Button
              data-testid="generate-payrolls-button"
              onClick={handleGeneratePayrolls}
              disabled={generating}
              variant="outline"
              className="font-ibm-plex"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Generazione..." : "Genera da Orari"}
            </Button>
            <Button
              data-testid="export-payroll-pdf"
              onClick={handleExportPDF}
              variant="outline"
              className="font-ibm-plex"
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta PDF
            </Button>
          </div>
        )}
      </div>

      {isPayAlert && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="pay-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm font-ibm-plex text-amber-800">
            <span className="font-semibold">Attenzione:</span> {unpaidCount} stipendi{unpaidCount > 1 ? "" : "o"} ancora non pagat{unpaidCount > 1 ? "i" : "o"} per {MONTH_NAMES[month - 1]} {year}.
          </p>
        </div>
      )}

      {/* Filters */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <Select value={filterCompany || "all"} onValueChange={(v) => { setFilterCompany(v === "all" ? "" : v); setFilterLocation(""); }}>
              <SelectTrigger data-testid="payroll-filter-company" className="font-ibm-plex">
                <SelectValue placeholder="Seleziona azienda" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le aziende</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterLocation || "none"} onValueChange={(v) => setFilterLocation(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="payroll-filter-location" className="font-ibm-plex">
                <SelectValue placeholder="Seleziona sede" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleziona sede...</SelectItem>
                {filteredLocations.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevMonth} data-testid="payroll-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 text-center font-ibm-plex font-medium">
                {MONTH_NAMES[month]} {year}
              </div>
              <Button variant="outline" size="icon" onClick={handleNextMonth} data-testid="payroll-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {filterLocation && Object.keys(payrolls).length > 0 && (
              <div className="text-right">
                <p className="text-xs text-[#52525B] font-ibm-plex">Totale Mese</p>
                <p className="text-lg font-bold font-ibm-plex-mono">{formatCurrency(calculateGrandTotal())}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payroll Table */}
      {!filterLocation ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">
            Seleziona una sede per visualizzare gli stipendi
          </CardContent>
        </Card>
      ) : loading ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">
            Caricamento...
          </CardContent>
        </Card>
      ) : employees.length === 0 ? (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-[#52525B] font-ibm-plex">
            Nessun dipendente trovato per questa sede
          </CardContent>
        </Card>
      ) : (
        <>
        <Card className="border border-[#E4E4E7]">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F4F4F5]">
                    <TableHead className="font-ibm-plex font-semibold">Dipendente</TableHead>
                    {isNovaService && (
                      <TableHead className="font-ibm-plex font-semibold text-right text-violet-700">Paga Lorda</TableHead>
                    )}
                    <TableHead className="font-ibm-plex font-semibold text-right">Paga Netta</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Ferie</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">ROL</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Malattia</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">104</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Lutto</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Matrim</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Cong P</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Km</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Spese</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Acconti</TableHead>
                    {isNovaService && (
                      <TableHead className="font-ibm-plex font-semibold text-right text-violet-700">Contributo NS</TableHead>
                    )}
                    <TableHead className="font-ibm-plex font-semibold text-right">Totale</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-center">Pagato</TableHead>
                    <TableHead className="font-ibm-plex font-semibold w-[80px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map(emp => {
                    const data = getPayrollData(emp.id);
                    return (
                      <TableRow key={emp.id} className="hover:bg-[#F4F4F5]" data-testid={`payroll-row-${emp.id}`}>
                        <TableCell className="font-ibm-plex font-medium">
                          {emp.last_name} {emp.first_name}
                        </TableCell>
                        {isNovaService && (
                          <TableCell className="font-ibm-plex-mono text-right text-violet-700">
                            {data.gross_pay ? formatCurrency(data.gross_pay) : "-"}
                          </TableCell>
                        )}
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.net_pay ? formatCurrency(data.net_pay) : "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.holiday_days_to_deduct || "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.rol_hours_to_deduct || "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.sick_days || "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.days_104 || "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.lutto_days || "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.matrimoniale_days || "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.congedo_parentale_days || "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.km_reimbursement ? formatCurrency(data.km_reimbursement) : "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.expense_reimbursement ? formatCurrency(data.expense_reimbursement) : "-"}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {data.advances_paid ? formatCurrency(data.advances_paid) : "-"}
                        </TableCell>
                        {isNovaService && (
                          <TableCell className="font-ibm-plex-mono text-right text-violet-700">
                            {data.nova_service_contribution ? formatCurrency(data.nova_service_contribution) : "-"}
                          </TableCell>
                        )}
                        <TableCell className="font-ibm-plex-mono text-right font-bold">
                          {data.total ? formatCurrency(data.total) : "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {data.net_pay ? (
                            <Checkbox
                              data-testid={`payroll-paid-${emp.id}`}
                              checked={!!data.is_paid}
                              onCheckedChange={() => handleTogglePaid(emp.id)}
                              className="mx-auto"
                            />
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-payroll-${emp.id}`}
                            onClick={() => handleOpenEditDialog(emp.id)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Nova Service Summary */}
        {isNovaService && novaServiceCalc && (
          <Card className="border-2 border-violet-200 bg-violet-50/30" data-testid="nova-service-summary">
            <CardHeader className="pb-3">
              <CardTitle className="font-outfit text-lg flex items-center gap-2 text-violet-900">
                <Calculator className="w-5 h-5" />
                Riepilogo Nova Service
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-violet-200">
                  <span className="font-ibm-plex text-sm text-[#52525B]">Totale Lordo Dipendenti</span>
                  <span className="font-ibm-plex-mono font-semibold" data-testid="ns-total-lordo">{formatCurrency(novaServiceCalc.totalLordo)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-violet-200">
                  <span className="font-ibm-plex text-sm text-[#52525B]">Contributo Nova Service</span>
                  <span className="font-ibm-plex-mono font-semibold text-violet-700" data-testid="ns-contributions">{formatCurrency(novaServiceCalc.totalContributions)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-violet-200">
                  <span className="font-ibm-plex text-sm text-[#52525B]">Sicurezza sul lavoro, rischi da interferenza</span>
                  <span className="font-ibm-plex-mono font-semibold" data-testid="ns-management-fee">{formatCurrency(novaServiceCalc.safetyFee)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-violet-200 gap-3">
                  <div className="flex-1">
                    <Input
                      data-testid="ns-management-label-input"
                      type="text"
                      value={nsManagementLabel}
                      onChange={(e) => setNsManagementLabel(e.target.value)}
                      onBlur={(e) => saveNsMonthly(novaServiceSafety, nsManagementFee, e.target.value)}
                      className="font-ibm-plex text-sm h-8 border-dashed"
                      placeholder="Nome voce personalizzata..."
                    />
                  </div>
                  <div className="w-32 shrink-0">
                    <Input
                      data-testid="ns-management-fee-input"
                      type="number"
                      step="0.01"
                      value={nsManagementFee !== null && nsManagementFee !== undefined ? nsManagementFee : ""}
                      onChange={(e) => setNsManagementFee(e.target.value ? parseFloat(e.target.value) : null)}
                      onBlur={(e) => {
                        const val = e.target.value ? parseFloat(e.target.value) : null;
                        saveNsMonthly(novaServiceSafety, val, nsManagementLabel);
                      }}
                      className="font-ibm-plex-mono text-right h-8 text-sm"
                      placeholder="0,00"
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-violet-200">
                  <span className="font-ibm-plex text-sm font-medium">Subtotale</span>
                  <span className="font-ibm-plex-mono font-bold" data-testid="ns-subtotal">{formatCurrency(novaServiceCalc.subtotal)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-violet-200">
                  <span className="font-ibm-plex text-sm text-[#52525B]">IVA 22%</span>
                  <span className="font-ibm-plex-mono font-semibold" data-testid="ns-iva">{formatCurrency(novaServiceCalc.iva)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-[#09090B] rounded-lg px-4 -mx-1">
                  <span className="font-outfit font-bold text-white">Totale Fattura</span>
                  <span className="font-ibm-plex-mono font-bold text-xl text-white" data-testid="ns-grand-total">{formatCurrency(novaServiceCalc.grandTotal)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Modifica Stipendio
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {isNovaService && (
              <div className="space-y-2">
                <Label className="font-ibm-plex text-violet-700">Paga Lorda (€)</Label>
                <Input
                  data-testid="payroll-gross-pay-input"
                  type="number"
                  step="0.01"
                  value={editForm.gross_pay}
                  onChange={(e) => setEditForm({ ...editForm, gross_pay: e.target.value })}
                  className="font-ibm-plex border-violet-300 focus:ring-violet-500"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="font-ibm-plex">Paga Netta (€)</Label>
              <Input
                data-testid="payroll-net-pay-input"
                type="number"
                step="0.01"
                value={editForm.net_pay}
                onChange={(e) => setEditForm({ ...editForm, net_pay: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Giorni Ferie</Label>
              <Input
                data-testid="payroll-holiday-input"
                type="number"
                step="0.5"
                value={editForm.holiday_days_to_deduct}
                onChange={(e) => setEditForm({ ...editForm, holiday_days_to_deduct: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Ore ROL</Label>
              <Input
                data-testid="payroll-rol-input"
                type="number"
                step="0.5"
                value={editForm.rol_hours_to_deduct}
                onChange={(e) => setEditForm({ ...editForm, rol_hours_to_deduct: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Giorni Malattia</Label>
              <Input
                data-testid="payroll-sick-input"
                type="number"
                value={editForm.sick_days}
                onChange={(e) => setEditForm({ ...editForm, sick_days: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Giorni 104</Label>
              <Input
                data-testid="payroll-104-input"
                type="number"
                value={editForm.days_104}
                onChange={(e) => setEditForm({ ...editForm, days_104: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Giorni Lutto</Label>
              <Input
                data-testid="payroll-lutto-input"
                type="number"
                value={editForm.lutto_days}
                onChange={(e) => setEditForm({ ...editForm, lutto_days: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Giorni Matrimoniale</Label>
              <Input
                data-testid="payroll-matrimoniale-input"
                type="number"
                value={editForm.matrimoniale_days}
                onChange={(e) => setEditForm({ ...editForm, matrimoniale_days: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Giorni Cong. Parentale</Label>
              <Input
                data-testid="payroll-congedo-input"
                type="number"
                value={editForm.congedo_parentale_days}
                onChange={(e) => setEditForm({ ...editForm, congedo_parentale_days: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Rimborso Km (€)</Label>
              <Input
                data-testid="payroll-km-input"
                type="number"
                step="0.01"
                value={editForm.km_reimbursement}
                onChange={(e) => setEditForm({ ...editForm, km_reimbursement: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Rimborso Spese (€)</Label>
              <Input
                data-testid="payroll-expense-input"
                type="number"
                step="0.01"
                value={editForm.expense_reimbursement}
                onChange={(e) => setEditForm({ ...editForm, expense_reimbursement: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Acconti Versati (€)</Label>
              <Input
                data-testid="payroll-advances-input"
                type="number"
                step="0.01"
                value={editForm.advances_paid}
                onChange={(e) => setEditForm({ ...editForm, advances_paid: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            {isNovaService && (
              <div className="space-y-2">
                <Label className="font-ibm-plex text-violet-700">Contributo Nova Service (€)</Label>
                <Input
                  data-testid="payroll-nova-contribution-input"
                  type="number"
                  step="0.01"
                  value={editForm.nova_service_contribution}
                  onChange={(e) => setEditForm({ ...editForm, nova_service_contribution: e.target.value })}
                  className="font-ibm-plex border-violet-300 focus:ring-violet-500"
                />
              </div>
            )}
            <div className="col-span-2 space-y-2">
              <Label className="font-ibm-plex">Note</Label>
              <Textarea
                data-testid="payroll-notes-input"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="font-ibm-plex"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="font-ibm-plex">
              Annulla
            </Button>
            <Button 
              data-testid="save-payroll-button"
              onClick={handleSavePayroll} 
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
